import { Label, RadioButton, RadioField, RadioGroup, Text } from 'react-aria-components';

import styles from './ThemeCards.module.css';

export interface ThemeCardOption {
  /** The value chosen. */
  id: string;
  /** The theme the preview shows: an ID from THEMES (ADR 0030). */
  theme: string;
  label: string;
  description: string;
}

export interface ThemeCardsProps {
  /** Always visible, above the cards. */
  label: string;
  options: readonly ThemeCardOption[];
  value: string;
  onChange: (id: string) => void;
  /** The mode the previews are drawn in: the one on screen now. */
  mode: 'light' | 'dark';
  description?: string;
  isDisabled?: boolean;
}

/**
 * Choosing a colour theme (ADR 0030): a radio group of cards, each with a small preview of
 * the app drawn in that theme. The preview sets data-theme and data-mode on itself, so the
 * colour tokens in tokens.css draw it; nothing here names a colour. Arrow keys move between
 * the cards, as in any radio group.
 */
export function ThemeCards({
  label,
  options,
  value,
  onChange,
  mode,
  description,
  isDisabled = false,
}: ThemeCardsProps) {
  return (
    <RadioGroup
      className={styles.group}
      value={value}
      onChange={onChange}
      isDisabled={isDisabled}
      orientation="horizontal"
    >
      <Label className={styles.label}>{label}</Label>
      {description === undefined ? null : (
        <Text slot="description" className={styles.description}>
          {description}
        </Text>
      )}
      <div className={styles.cards}>
        {options.map((option) => (
          <RadioField key={option.id} value={option.id} className={styles.field}>
            <RadioButton className={styles.card}>
              <span className={styles.preview} data-theme={option.theme} data-mode={mode}>
                <span className={styles.previewSidebar}>
                  <span className={styles.previewMark} />
                  <span className={styles.previewNavCurrent} />
                  <span className={styles.previewNav} />
                  <span className={styles.previewNav} />
                </span>
                <span className={styles.previewMain}>
                  <span className={styles.previewHeader} />
                  <span className={styles.previewPanel}>
                    <span className={styles.previewCard}>
                      <span className={styles.previewLine} />
                      <span className={styles.previewTags}>
                        <span className={styles.previewTagDone} />
                        <span className={styles.previewTagWaiting} />
                      </span>
                      <span className={styles.previewButton} />
                    </span>
                  </span>
                </span>
              </span>
              <span className={styles.text}>
                <span className={styles.name}>
                  <span className={styles.circle} aria-hidden="true" />
                  {option.label}
                </span>
                <span className={styles.optionDescription}>{option.description}</span>
              </span>
            </RadioButton>
          </RadioField>
        ))}
      </div>
    </RadioGroup>
  );
}
